import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useLearner } from '../hooks/useLearner';
import useFocusOnMount from '../hooks/useFocusOnMount';
import { getRecommendedBand, switchCurriculum } from '../api/learners';
import { ALL_CURRICULA } from '../constants/curricula';
import CloudBand from '../components/common/CloudBand';
import WanderWordsLogo from '../components/common/WanderWordsLogo';
import OnboardingCard from '../components/common/OnboardingCard';
import OnboardingPageCard from '../components/common/OnboardingPageCard';
import ToolTipLing from '../components/common/ToolTipLing';
import shared from './onboarding/OnboardingPages.module.css';
import s from './SwitchCurriculumPage.module.css';

export default function SwitchCurriculumPage() {
  const { t } = useTranslation();
  const mainRef = useFocusOnMount();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const { data: activeLearner } = useLearner();

  const currentSlug = activeLearner?.curriculum?.slug || null;

  // Two-step flow: 'select' → 'confirm'.
  const [step, setStep] = useState('select');
  const [pendingSlug, setPendingSlug] = useState(null);
  const [recommendedBand, setRecommendedBand] = useState(null);
  const [isLoadingBand, setIsLoadingBand] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const pendingCurriculumDef = useMemo(
    () => ALL_CURRICULA.find((c) => c.slug === pendingSlug),
    [pendingSlug]
  );

  function handleBackToSettings() {
    navigate('/settings', { replace: true });
  }

  async function handleSelectCurriculum(slug) {
    if (slug === currentSlug) return;
    setPendingSlug(slug);
    setRecommendedBand(null);
    setError(null);
    setStep('confirm');
    if (!activeLearner?.id) return;
    setIsLoadingBand(true);
    try {
      const res = await getRecommendedBand(activeLearner.id, slug);
      setRecommendedBand(res?.recommended_band || 'beginner');
    } catch (err) {
      setError(err.message || t('common.somethingWentWrong'));
    } finally {
      setIsLoadingBand(false);
    }
  }

  function handleBackToSelect() {
    setStep('select');
    setError(null);
  }

  async function handleConfirm() {
    if (!activeLearner?.id || !pendingSlug) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const res = await switchCurriculum(activeLearner.id, pendingSlug);
      // FP-LEARNER-STORE-RETIREMENT-01: optimistically merge the RPC response
      // into the ['learners'] cache so the next render of /settings sees the
      // new curriculum + band immediately. Then invalidate so a background
      // refetch reconciles. Awaiting only the refetch before navigate would
      // race the RPC's commit and could briefly read pre-switch state.
      queryClient.setQueryData(['learners'], (prev) =>
        Array.isArray(prev)
          ? prev.map((l) =>
              l.id === activeLearner.id
                ? { ...l, curriculum: res.curriculum, placement_band: res.recommended_band }
                : l
            )
          : prev
      );
      queryClient.invalidateQueries({ queryKey: ['learners'] });
      // The dashboard's useLearnerProfile uses ['learnerProfile', id] for the
      // detail payload — invalidate that too so progress fields refresh.
      queryClient.invalidateQueries({ queryKey: ['learnerProfile', activeLearner.id] });
      // FP-CURRICULUM-SWITCH-PLACED-AT-01: ['me'] carries onboarding_complete,
      // which the OnboardingGuard reads to decide whether to push the eval
      // flow. Invalidate it so the next render sees fresh server state.
      queryClient.invalidateQueries({ queryKey: ['me'] });
      navigate('/settings', { replace: true });
    } catch (err) {
      setError(err.message || t('common.somethingWentWrong'));
      setIsSubmitting(false);
    }
  }

  // ─── Selection view content ───
  const selectInfo = (
    <div className={s.infoBanner} role="status">
      {t('switchCurriculum.progressInfo')}
    </div>
  );

  const selectCards = (
    <div className={shared.curriculumScroll}>
      <div className={shared.optionCards} role="radiogroup">
        {ALL_CURRICULA.map((c) => {
          const isCurrent = c.slug === currentSlug;
          if (isCurrent) {
            return (
              <div
                key={c.slug}
                className={s.currentCard}
                aria-disabled="true"
              >
                <span className={s.currentBadge}>{t('switchCurriculum.currentBadge')}</span>
                <span className={s.currentTitle}>{t(c.nameKey)}</span>
                <span className={s.currentDesc}>{t(c.descKey)}</span>
              </div>
            );
          }
          return (
            <OnboardingCard
              key={c.slug}
              title={t(c.nameKey)}
              description={t(c.descKey)}
              selected={false}
              onClick={() => handleSelectCurriculum(c.slug)}
            />
          );
        })}
      </div>
    </div>
  );

  const selectActions = (
    <button
      type="button"
      onClick={handleBackToSettings}
      className={shared.cancelBtn}
    >
      {t('common.cancel')}
    </button>
  );

  // ─── Confirmation view content ───
  const confirmBody = pendingCurriculumDef ? (
    <div className={s.confirmBlock}>
      <p className={s.confirmCurriculumName}>{t(pendingCurriculumDef.nameKey)}</p>
      <p className={s.confirmRecLabel}>
        {isLoadingBand
          ? t('switchCurriculum.recommendedBandLoading')
          : recommendedBand
            ? t('switchCurriculum.recommendedBandResolved', {
                band: t(`switchCurriculum.band.${recommendedBand}`),
              })
            : t('switchCurriculum.recommendedBandPrompt')}
      </p>
    </div>
  ) : null;

  const confirmActions = (
    <div className={shared.btnRow}>
      <button
        type="button"
        onClick={handleBackToSelect}
        disabled={isSubmitting}
        className={shared.cancelBtn}
      >
        {t('common.back')}
      </button>
      <button
        type="button"
        onClick={handleConfirm}
        disabled={isSubmitting}
        className={shared.continueBtn}
      >
        {isSubmitting ? t('common.saving') : t('switchCurriculum.confirmCta')}
      </button>
    </div>
  );

  // ─── Header content (title + subtitle) ───
  const isSelectStep = step === 'select';
  const headerTitle = isSelectStep
    ? t('switchCurriculum.selectTitle')
    : t('switchCurriculum.confirmTitle');
  const headerSubtitle = isSelectStep
    ? t('switchCurriculum.selectSubtitle', { name: activeLearner?.name || t('common.learner') })
    : t('switchCurriculum.confirmSubtitle');

  return (
    <main
      className={shared.page}
      style={{ '--cloud-color': '#2AB8A8' }}
      ref={mainRef}
      tabIndex={-1}
    >
      {/* Desktop decoration */}
      <div className={`${shared.topBand} ${shared.topBandTeal}`} />
      <CloudBand variant="teal" className={shared.cloudBandTeal} />
      <WanderWordsLogo className={shared.logo} />

      {/* ───────────── MOBILE / TABLET LAYOUT (hidden ≥1200px) ───────────── */}
      <div className={shared.mobileOnly}>
        <div className={shared.content}>
          <div className={shared.contentRow}>
            <div className={`${shared.leftCol} ${shared.leftColTeal}`}>
              <div className={shared.headerRow}>
                <div className={shared.headerColumn}>
                  <h1 className={shared.pageTitle}>{headerTitle}</h1>
                  <p className={shared.subtitle}>{headerSubtitle}</p>
                </div>
                <img
                  src="/assets/mascot-ling-happy.webp"
                  alt="Ling the cat"
                  className={shared.mascotImg}
                  style={{ width: '108px' }}
                />
              </div>
            </div>
          </div>

          <div className={shared.mobileCreamSection}>
            <div className={shared.rightCol}>
              {error && (
                <div className={shared.errorBanner} role="alert">{error}</div>
              )}
              {isSelectStep ? (
                <>
                  {selectInfo}
                  {selectCards}
                </>
              ) : (
                confirmBody
              )}
              <div className={shared.spacer} />
            </div>

            <div className={shared.stickyBtnArea}>
              {isSelectStep ? selectActions : confirmActions}
            </div>
          </div>
        </div>
      </div>

      {/* ───────────── DESKTOP CARD (≥1200px only) ───────────── */}
      <OnboardingPageCard
        leftWidth={392}
        leftContent={
          <>
            <div className={shared.headerColumn}>
              <h1 className={shared.pageTitle}>{headerTitle}</h1>
              <p className={shared.subtitle}>{headerSubtitle}</p>
            </div>
            <ToolTipLing type="curious">
              {isSelectStep
                ? t('switchCurriculum.tooltipSelect')
                : t('switchCurriculum.tooltipConfirm')}
            </ToolTipLing>
          </>
        }
        rightContent={
          <>
            {error && (
              <div className={shared.errorBanner} role="alert">{error}</div>
            )}
            {isSelectStep ? (
              <>
                {selectInfo}
                {selectCards}
                {selectActions}
              </>
            ) : (
              <>
                {confirmBody}
                {confirmActions}
              </>
            )}
          </>
        }
      />
    </main>
  );
}
